<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import * as XLSX from 'xlsx'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import { mapVhqRowsToSheet, formatVhqDate, type VhqExportRow } from '@/utils/vhqExport'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'vhq-list',
  data() {
    return {
      rows: [] as VhqExportRow[],
      filterText: '',
      filterPackage: '',
      filterStatus: '',
      filterCompanyType: '',
      filterDateFrom: '',
      filterDateTo: '',
      loaded: false,
      exporting: false,
      currentPage: 1,
      lastPage: 1,
      total: 0,
      searchDebounceTimer: null as ReturnType<typeof setTimeout> | null,
      router: useRouter(),
    }
  },
  watch: {
    filterPackage() { this.resetAndLoad() },
    filterStatus() { this.resetAndLoad() },
    filterCompanyType() { this.resetAndLoad() },
    filterDateFrom() { this.resetAndLoad() },
    filterDateTo() { this.resetAndLoad() },
    filterText() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => this.resetAndLoad(), 400)
    },
  },
  methods: {
    async loadData() {
      this.loaded = false
      try {
        const res: ResponseData = await HeadquartersDataService.getVhqOverview(
          this.currentPage,
          this.filterText,
          this.filterPackage,
          this.filterStatus,
          this.filterCompanyType,
          this.filterDateFrom,
          this.filterDateTo
        )
        const pagination = res.data.data
        this.rows = pagination.data
        this.currentPage = pagination.current_page
        this.lastPage = pagination.last_page
        this.total = pagination.total
      } catch (e: any) {
        toast.error('Chyba: ' + e.message)
      } finally {
        this.loaded = true
      }
    },
    resetAndLoad() {
      this.currentPage = 1
      this.loadData()
    },
    changePage(page: number) {
      if (page < 1 || page > this.lastPage) return
      this.currentPage = page
      this.loadData()
    },
    async exportToExcel() {
      this.exporting = true
      try {
        const res: ResponseData = await HeadquartersDataService.getVhqExport(
          this.filterText,
          this.filterPackage,
          this.filterStatus,
          this.filterCompanyType,
          this.filterDateFrom,
          this.filterDateTo
        )
        const rows: VhqExportRow[] = res.data.data
        if (!rows.length) {
          toast.warning('Žiadne záznamy na export')
          return
        }
        const worksheet = XLSX.utils.aoa_to_sheet(mapVhqRowsToSheet(rows))
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'VHQ')
        const date = new Date().toISOString().slice(0, 10)
        XLSX.writeFile(workbook, `vhq-export-${date}.xlsx`)
      } catch (e: any) {
        toast.error('Chyba: ' + e.message)
      } finally {
        this.exporting = false
      }
    },
    getDateStatus(date: string | null): 'expired' | 'warning' | 'ok' | 'none' {
      if (!date) return 'none'
      const expiry = new Date(date)
      const now = new Date()
      const warningDate = new Date()
      warningDate.setDate(warningDate.getDate() + 15)
      if (expiry < now) return 'expired'
      if (expiry <= warningDate) return 'warning'
      return 'ok'
    },
    goToDetail(id: number) {
      this.router.push({ name: 'VHQ Detail', params: { id } })
    },
    formatVhqDate(date: string | null): string {
      return formatVhqDate(date)
    },
  },
  mounted() {
    this.loadData()
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <h1 class="text-2xl font-semibold text-gray-900">Virtuálne sídla</h1>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-4">

        <!-- Filters -->
        <div class="flex flex-col sm:flex-row gap-3 mb-4">
          <div class="relative flex-1 max-w-xs">
            <label for="vhq-search" class="sr-only">Hľadať</label>
            <input
              id="vhq-search"
              v-model="filterText"
              type="search"
              placeholder="Hľadať podľa názvu alebo IČO..."
              class="block w-full p-2 pl-9 text-sm border border-gray-200 rounded-md focus:border-teal-500 focus:ring-teal-500"
            />
            <svg class="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
          </div>
          <select v-model="filterPackage" class="border border-gray-200 rounded-md text-sm pl-3 pr-8 py-2 focus:border-teal-500 focus:ring-teal-500">
            <option value="">Všetky balíky</option>
            <option value="Mini">Mini</option>
            <option value="Ideal">Ideal</option>
            <option value="Premium">Premium</option>
            <option value="Extra">Extra</option>
            <option value="Individual">Individual</option>
            <option value="Základný trvalý pobyt v Bratislave">Trvalý pobyt - Základný</option>
            <option value="Najlepší trvalý pobyt v Bratislave">Trvalý pobyt - Najlepší</option>
          </select>
          <select v-model="filterStatus" class="border border-gray-200 rounded-md text-sm pl-3 pr-8 py-2 focus:border-teal-500 focus:ring-teal-500">
            <option value="">Všetky stavy</option>
            <option value="active">Aktívne</option>
            <option value="expired">Expirované</option>
          </select>
          <select v-model="filterCompanyType" class="border border-gray-200 rounded-md text-sm pl-3 pr-8 py-2 focus:border-teal-500 focus:ring-teal-500">
            <option value="">Všetky typy</option>
            <option value="1">Fyzická osoba</option>
            <option value="2">Právnická osoba</option>
            <option value="3">Trvalý pobyt</option>
          </select>
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-500 whitespace-nowrap">Zaplatené od</label>
            <input
              v-model="filterDateFrom"
              type="date"
              class="border border-gray-200 rounded-md text-sm px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
            />
          </div>
          <div class="flex items-center gap-2">
            <label class="text-sm text-gray-500 whitespace-nowrap">do</label>
            <input
              v-model="filterDateTo"
              type="date"
              class="border border-gray-200 rounded-md text-sm px-3 py-2 focus:border-teal-500 focus:ring-teal-500"
            />
          </div>
          <button
            type="button"
            :disabled="exporting"
            class="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="exportToExcel"
          >
            <svg v-if="exporting" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            {{ exporting ? 'Exportujem...' : 'Export do Excelu' }}
          </button>
        </div>

        <!-- Loading -->
        <div v-if="!loaded" class="flex justify-center py-16">
          <svg class="animate-spin h-8 w-8 text-teal-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
        </div>

        <div v-else class="overflow-x-auto rounded-lg shadow">
          <table class="min-w-full divide-y divide-gray-200 bg-white">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID Subjektu</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subjekt</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">IČO</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Adresa</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Balík</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Zaplatené do</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Aktívne na adrese</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Označená schránka</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Poznámka</th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Akcie</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-if="rows.length === 0">
                <td colspan="10" class="px-4 py-8 text-center text-sm text-gray-400">Žiadne záznamy</td>
              </tr>
              <tr
                v-for="row in rows"
                :key="row.company_id"
                class="hover:bg-teal-50 transition-colors cursor-pointer"
                @click="goToDetail(row.company_id)"
              >
                <td class="px-4 py-3 text-sm text-gray-700">{{ row.company_id }}</td>
                <td class="px-4 py-3 text-sm font-medium text-gray-900">{{ row.name }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ row.ico || '—' }}</td>
                <td class="px-4 py-3 text-sm text-gray-600">{{ row.owner_name || '—' }}</td>
                <td class="px-4 py-3 text-sm">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                    :class="{
                      'bg-gray-100 text-gray-600': row.sidlo_typ_balika === 'Mini',
                      'bg-teal-100 text-teal-700': row.sidlo_typ_balika === 'Ideal',
                      'bg-blue-100 text-blue-700': row.sidlo_typ_balika === 'Premium',
                      'bg-purple-100 text-purple-700': row.sidlo_typ_balika === 'Extra',
                      'bg-amber-100 text-amber-700': row.sidlo_typ_balika === 'Individual',
                    }">
                    {{ row.sidlo_typ_balika || '—' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm font-medium" :class="{
                  'bg-red-100 text-red-700': getDateStatus(row.sidlo_zaplatene_do) === 'expired',
                  'bg-orange-100 text-orange-700': getDateStatus(row.sidlo_zaplatene_do) === 'warning',
                  'text-gray-600': getDateStatus(row.sidlo_zaplatene_do) === 'ok' || getDateStatus(row.sidlo_zaplatene_do) === 'none',
                }">{{ formatVhqDate(row.sidlo_zaplatene_do) }}</td>
                <td class="px-4 py-3 text-sm text-center">
                  <span v-if="row.active_on_address == true && row.active_on_address != null" class="text-green-500" title="Áno">✓</span>
                  <span v-else-if="row.active_on_address == false && row.active_on_address != null" class="text-red-400" title="Nie">✗</span>
                  <span v-else class="text-gray-300" title="Nenastavené">—</span>
                </td>
                <td class="px-4 py-3 text-sm text-center">
                  <span v-if="row.marked_mailbox == true && row.marked_mailbox != null" class="text-green-500" title="Áno">✓</span>
                  <span v-else-if="row.marked_mailbox == false && row.marked_mailbox != null" class="text-red-400" title="Nie">✗</span>
                  <span v-else class="text-gray-300" title="Nenastavené">—</span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate" :title="row.custom_note || ''">
                  {{ row.custom_note || '—' }}
                </td>
                <td class="px-4 py-3 text-sm" @click.stop>
                  <button
                    type="button"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-teal-500"
                    @click="goToDetail(row.company_id)"
                  >
                    Detail
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="px-4 py-3 flex items-center justify-between bg-white border-t border-gray-100">
            <span class="text-xs text-gray-400">Zobrazených {{ rows.length }} z {{ total }} záznamov</span>
            <div class="flex items-center gap-1">
              <button
                type="button"
                :disabled="currentPage === 1"
                @click="changePage(currentPage - 1)"
                class="px-3 py-1 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >‹</button>
              <span class="px-3 py-1 text-sm text-gray-700">{{ currentPage }} / {{ lastPage }}</span>
              <button
                type="button"
                :disabled="currentPage === lastPage"
                @click="changePage(currentPage + 1)"
                class="px-3 py-1 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >›</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
